/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { WINDOW } from '@myprofile-ui/pidx-utils'
import { UntypedFormGroup, Validators } from '@angular/forms'
import {
  MyProfileListMode,
  MyProfilePhoneFormService,
  PhoneNumberService,
  countryPrefixValidator,
  duplicateNumber,
  phoneValidator,
} from '@myprofile-ui/myprofile-phone-utils'
import { PidxLoaderModeEnum, PidxPdsModalWrapperService } from '@myprofile-ui/pidx-ui-components'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { ToastManager } from '@porsche-design-system/components-angular'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { Observable } from 'rxjs'
import { MyProfileWidgetMode } from '../../enums/myprofile-widget-mode'
import { CreatePhone, DeletePhone, ShowBlankEditor, UpdatePhone } from '../../store/editor.actions'
import { SetPhoneBeingVerified } from '../../store/list.actions'
import { PhoneWidgetState } from '../../store/phone-widget.state'
import { PhoneCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/phone/phone.service'

@Component({
  selector: 'myprofile-phone-editform',
  templateUrl: './myprofile-phone-editform.component.html',
  host: { class: 'profile:block profile:relative', '[attr.tabindex]': '-1' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfilePhoneEditFormComponent implements OnInit {
  private store = inject(Store)
  private changeDetector = inject(ChangeDetectorRef)
  private _translateService = inject(TranslateService)
  private _store = inject(Store)
  private phoneFormService = inject(MyProfilePhoneFormService)
  private phoneNumberService = inject(PhoneNumberService)
  private pidxPdsModalWrapperService = inject(PidxPdsModalWrapperService)
  private toastManager = inject(ToastManager)
  private window = inject<Window>(WINDOW)

  @Input() editorLoadingLabel!: string
  @Input() mode?: MyProfileListMode
  @Input() hasConfirmedVehicle = false

  isBlankCheckoutModeEditor$: Observable<boolean> = inject(Store).select(PhoneWidgetState.isBlankCheckoutModeEditor)
  editorIsLoading$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.editor.loading)
  errorMessage$: Observable<string | null> = inject(Store).select(({ phoneWidget }) => phoneWidget.editor.errorMessage)

  public formGroup!: UntypedFormGroup
  public phoneWhenEnteredTheEditMode: PhoneCommunicationDto | null | undefined = this.store.selectSnapshot(
    (state) => state.phoneWidget.editor.tmpPhone,
  )
  public widgetMode: MyProfileWidgetMode = this.store.selectSnapshot((state) => state.phoneWidget.mode)
  public phoneIdBeingEdited = this.store.selectSnapshot((state) => state.phoneWidget.phoneIdBeingEdited)
  public phones = this.store.selectSnapshot((state) => state.phoneWidget.phones)
  public isLastMobileNumber = false
  public isLastStandard = false
  public editedPhone: PhoneCommunicationDto | undefined
  public country = this.store.selectSnapshot((state) => state.phoneWidget.country)
  @Output() goToChangePorscheIdPageEvent: EventEmitter<any> = new EventEmitter<any>()

  get deleteButtonPhoneNumberForAria(): string {
    if (!this.editedPhone?.number) {
      return ''
    }
    return this.phoneNumberService.getFormattedPhoneNumber(this.editedPhone.number, this.country)
  }

  get phoneNumberFormControlErrorMessage() {
    const phoneNumberFormControl = this.formGroup.get('number')!

    if (phoneNumberFormControl.valid || !phoneNumberFormControl.touched) {
      return false
    }

    if (phoneNumberFormControl.errors?.required) {
      return this._translateService.instant('pidpaw.editPhone.error.inputRequired')
    }

    if (phoneNumberFormControl.errors?.phoneValidation) {
      return this._translateService.instant('pidpaw.editPhone.error.phoneInput')
    }

    if (phoneNumberFormControl.errors?.countryPrefix) {
      return this._translateService.instant('pidpaw.editPhone.error.countryPrefix')
    }

    if (phoneNumberFormControl.errors?.duplicateNumber) {
      return this._translateService.instant('pidpaw.editPhone.error.duplicate')
    }
  }

  get isSaveAndVerify(): boolean {
    if (this.formGroup.get('phoneType')!.value !== 'mobile') {
      return false
    }

    // # This is the first number then
    if (this.phoneWhenEnteredTheEditMode?.isVerified) {
      return !this.isSameNumber()
    }
    return true
  }
  get isUSCountry(): boolean {
    return this.country.toLowerCase() === 'us'
  }

  static triggerFormValidationErrors(formGroup: UntypedFormGroup) {
    Object.entries(formGroup.controls)
      .filter(([_, control]) => !control.disabled)
      .forEach(([controlName]) => {
        const element = document.querySelector(`[name="${controlName}"]`) as HTMLElement | null
        if (element) {
          element.focus()
          element.blur()
        }
      })

    Object.values(formGroup.controls)
      .filter((ctrl) => !ctrl.disabled)
      .forEach((ctrl) => {
        ctrl.markAllAsTouched()
      })
  }

  ngOnInit() {
    this.setupForm()
    this.addValidatorsAndPossiblePrefix()
  }

  selectType(e: Event) {
    const type = (e.target as HTMLSelectElement)?.value
    this.formGroup.patchValue({ phoneType: type })

    // We need to reset the isStandard when the users switches types
    if (this.phones.length === 0) {
      this.formGroup.patchValue({ isStandard: true })
    }

    this.formGroup.get('number')!.updateValueAndValidity()
  }

  async submitForm(formGroup: UntypedFormGroup) {
    if (formGroup.invalid) {
      MyProfilePhoneEditFormComponent.triggerFormValidationErrors(formGroup)
      return
    }

    const phoneData: PhoneCommunicationDto = this.phoneFormService.preparePhonePayload(formGroup)

    // We're updating an existing phone
    if (phoneData.id) {
      this.store.dispatch(new UpdatePhone(phoneData)).subscribe(() => {
        this.decideAndOpenVerifyModal()
      })
    } else {
      this.store.dispatch(new CreatePhone(phoneData)).subscribe(() => {
        this.decideAndOpenVerifyModal()
      })
    }
  }

  shouldShowVerificationDescription(): boolean {
    // Early return for landline numbers
    if (this.formGroup.get('phoneType')!.value === 'landline') {
      return false
    }
    // New mobile number
    if (!this.phoneWhenEnteredTheEditMode) {
      return true
    }
    // Edit of unverified mobile number
    if (!this.editedPhone?.isVerified) {
      return true
    }
    // Number has changed in edit
    return this.formGroup.get('number')!.value !== this.phoneWhenEnteredTheEditMode?.number
  }

  isSameNumber(): boolean {
    return this.formGroup.get('number')!.value === this.phoneWhenEnteredTheEditMode?.number
  }

  deleteThisPhone(event: Event) {
    // Stop propagating the click event to the formGroup
    event.preventDefault()
    if (!this.editedPhone) {
      return
    }
    this.prepareDeleteItem(this.editedPhone, this.isLastMobileNumber)
  }

  cancel() {
    const prevEditedId = this.store.selectSnapshot((s) => s.phoneWidget.phoneIdBeingEdited)
    this.store.dispatch(new ShowBlankEditor(false))
    if (!prevEditedId) {
      this.setFocusOnElementId('add-phone-number-button')
    }
  }

  private async addValidatorsAndPossiblePrefix() {
    const control = this.formGroup.get('number')!
    control.setValidators(
      Validators.compose([
        countryPrefixValidator(),
        duplicateNumber(this.phones, this.phoneIdBeingEdited, this.phoneFormService.getStringWithoutWhitespaces),
        phoneValidator(this.country),
        Validators.required,
      ]),
    )

    if (this.country && !control.value) {
      const countryCode = this.phoneNumberService.getCountryCodeForRegionCode(this.country)
      control.setValue(countryCode)
      control.updateValueAndValidity()
    }
  }

  private setupForm() {
    this.editedPhone = this.store.selectSnapshot((state) => state.phoneWidget.editor.tmpPhone)

    if (this.editedPhone) {
      this.isLastMobileNumber = this.phoneFormService.isLastMobileNumber(this.editedPhone, this.phones)
    }

    // Needed since we disable the unset of the standard-number, since there always has to be one standard.
    if (this.editedPhone && this.editedPhone.isStandard) {
      this.isLastStandard = true
    }

    if (this.editedPhone) {
      this.formGroup = this.phoneFormService.createPrefilledPhoneForm(this.editedPhone, this.phones.length === 0 || this.isLastStandard)
    } else {
      this.formGroup = this.phoneFormService.createDefaultPhoneForm()
    }

    if (this.phones.length === 0) {
      this.formGroup.patchValue({ isStandard: true })
    }

    this.changeDetector.detectChanges()
  }

  private async decideAndOpenVerifyModal() {
    if (this.isSaveAndVerify && this.hasConfirmedVehicle) {
      const phones = this.store.selectSnapshot(({ phoneWidget }) => phoneWidget.phones)

      const formattedNumber = parsePhoneNumber(this.formGroup.get('number')!.value).number?.e164 ?? ''
      // We're setting the phone number here not the ID cause if that's a newly added number, we dont have an ID yet.
      this.store.dispatch(
        new SetPhoneBeingVerified({
          number: formattedNumber,
          section: phones.length === 1 ? MyProfileListMode.ONLY_STANDARD : MyProfileListMode.ONLY_ADDITIONAL,
          preferred: this.formGroup.get('isStandard')!.value,
          id: phones.find((phone: PhoneCommunicationDto) => phone.number === formattedNumber)?.id,
          phoneType: phones.find((phone: PhoneCommunicationDto) => phone.number === formattedNumber)?.phoneType,
        }),
      )
    } else {
      this.toastManager.addMessage({ text: this._translateService.instant('pidp.generic.successfulySaved'), state: 'success' })
    }
  }

  public prepareDeleteItem(item: PhoneCommunicationDto, isLastPhoneNumber: boolean) {
    if (isLastPhoneNumber && item.isVerified) {
      this.showDeleteLastPhoneModal()
    } else {
      this.showDeletePromptModal(item)
    }
  }

  private async showDeleteLastPhoneModal() {
    const body = this._translateService.instant('pidpaw.deletePhone.error.deleteLast')
    const btnPrimary = this._translateService.instant('pidpaw.deletePhone.button.ok')

    await this.pidxPdsModalWrapperService.openModal({ body, btnPrimary })
    this.setFocusOnElementId('add-phone-number-button')
  }

  private async showDeletePromptModal(item: PhoneCommunicationDto) {
    const title = this._translateService.instant('pidpaw.editPhone.deleteConfirmationModal.title')
    const body = `\u2066${item.number}\u2069`
    const btnPrimary = this._translateService.instant('pidpaw.editPhone.deleteConfirmationModal.btnPrimary')
    const btnGhost = this._translateService.instant('pidpaw.editPhone.deleteConfirmationModal.btnGhost')

    const result = await this.pidxPdsModalWrapperService.openModal({ title, body, btnPrimary, btnGhost })
    if (result) {
      this._store.dispatch(new DeletePhone(item))
    }
    this.setFocusOnElementId('add-phone-number-button')
  }

  private setFocusOnElementId(id: string) {
    this.window.setTimeout(() => {
      this.window.document.getElementById(id)?.focus()
    }, 0)
  }

  public goToChangePorscheIdPage() {
    this.goToChangePorscheIdPageEvent.emit()
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
